/**
 * FR-ECR-03 — Extourne des écritures de paie.
 *
 * Règle : une période comptable clôturée n'est JAMAIS modifiée. Corriger un bulletin dont les écritures
 * sont dans une période clôturée = extourner les écritures d'origine (miroir exact, dans la première
 * période ouverte) puis comptabiliser le bulletin corrigé dans cette même période. Même principe que
 * l'annulation d'une facture (source `extourne`, `groupe_id` = écriture d'origine).
 */
import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Salarie } from '@shared/types/Salarie';
import { uuid } from '@shared/utils/uuid';
import { roundFCFA } from '@shared/utils/round';
import { type TableComptesPaie } from './ComptesPaie';
import { EcrituresPaieGenerator } from './EcrituresPaieGenerator';
import { PieceDesequilibreeError, totauxPiece } from './EquilibrePiece';

export class ModificationPeriodeClotureeError extends Error {
  constructor(public readonly periode: string) {
    super(
      `La période ${periode} est clôturée : ses écritures de paie ne se modifient pas, elles s'extournent (correction imputée sur une période ouverte).`,
    );
    this.name = 'ModificationPeriodeClotureeError';
  }
}

const FORMAT_PERIODE = /^\d{4}-\d{2}$/;

/** Période d'un bulletin à laquelle se rattache une écriture de paie (origine si correction). */
export function periodeBulletinDeEcriture(e: Pick<Ecriture, 'periode' | 'periode_origine'>): string {
  return e.periode_origine ?? e.periode;
}

/** Première période ouverte à partir de `periode` (incluse). */
export function premierePeriodeOuverte(periode: string, cloturees: readonly string[]): string {
  if (!FORMAT_PERIODE.test(periode)) throw new Error(`Période invalide : ${periode}`);
  const fermees = new Set(cloturees);
  let [y, m] = periode.split('-').map(Number) as [number, number];
  for (let i = 0; i < 600; i += 1) {
    const p = `${y}-${String(m).padStart(2, '0')}`;
    if (!fermees.has(p)) return p;
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  throw new Error('Aucune période ouverte trouvée.');
}

/** Refuse toute suppression/modification d'écritures situées dans une période clôturée. */
export function assertEcrituresModifiables(
  ecritures: readonly Pick<Ecriture, 'periode'>[],
  cloturees: readonly string[],
): void {
  const fermees = new Set(cloturees);
  for (const e of ecritures) {
    if (fermees.has(e.periode)) throw new ModificationPeriodeClotureeError(e.periode);
  }
}

function dernierJour(periode: string): string {
  const [y, m] = periode.split('-').map(Number) as [number, number];
  const last = new Date(y, m, 0).getDate();
  return `${String(last).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
}

export interface ParametresExtournePaie {
  /** Journal complet (pour repérer ce qui est déjà extourné). */
  ecritures: readonly Ecriture[];
  salarie_id: string;
  /** Période du bulletin dont on extourne les écritures. */
  periodeBulletin: string;
  /** Période ouverte qui reçoit l'extourne. */
  periodeImputation: string;
  genere_par?: string;
}

/** Écritures de paie d'un salarié et d'une période, encore « vivantes » (non extournées). */
export function ecrituresPaieVivantes(
  ecritures: readonly Ecriture[],
  salarie_id: string,
  periodeBulletin: string,
): Ecriture[] {
  const extournees = new Set(
    ecritures.filter((e) => e.source === 'extourne' && e.groupe_id).map((e) => e.groupe_id as string),
  );
  return ecritures.filter(
    (e) =>
      e.salarie_id === salarie_id &&
      periodeBulletinDeEcriture(e) === periodeBulletin &&
      (e.source === 'paie' || e.source === 'paie_correction') &&
      !extournees.has(e.id),
  );
}

/** Extourne miroir (débit ↔ crédit) des écritures de paie vivantes du bulletin. */
export function extournerEcrituresPaie(p: ParametresExtournePaie): Ecriture[] {
  if (!FORMAT_PERIODE.test(p.periodeImputation)) {
    throw new Error(`Période d'imputation invalide : ${p.periodeImputation}`);
  }
  const vivantes = ecrituresPaieVivantes(p.ecritures, p.salarie_id, p.periodeBulletin);
  const dateEcr = dernierJour(p.periodeImputation);
  const extournes = vivantes.map<Ecriture>((e) => {
    const x: Ecriture = {
      ...e,
      id: uuid('ecr'),
      date: dateEcr,
      periode: p.periodeImputation,
      periode_origine: p.periodeBulletin,
      debit: e.credit,
      credit: e.debit,
      libelle: `Extourne — ${e.libelle}`,
      source: 'extourne',
      groupe_id: e.id,
      genere_le: new Date().toISOString(),
    };
    if (p.genere_par) x.genere_par = p.genere_par;
    return x;
  });
  const { debit, credit } = totauxPiece(extournes);
  if (debit !== credit) throw new PieceDesequilibreeError(debit, credit);
  return extournes;
}

export interface CorrectionPaie {
  extournes: Ecriture[];
  corrections: Ecriture[];
}

/**
 * Correction d'un bulletin dont la période comptable est clôturée : extourne des écritures d'origine
 * + écritures du bulletin corrigé, toutes deux imputées sur `periodeImputation` (ouverte).
 */
export function corrigerEcrituresPaie(p: {
  ecritures: readonly Ecriture[];
  bulletinCorrige: Bulletin;
  salarie: Salarie;
  periodeImputation: string;
  comptes?: TableComptesPaie;
  genere_par?: string;
}): CorrectionPaie {
  const extournes = extournerEcrituresPaie({
    ecritures: p.ecritures,
    salarie_id: p.bulletinCorrige.salarie_id,
    periodeBulletin: p.bulletinCorrige.periode,
    periodeImputation: p.periodeImputation,
    genere_par: p.genere_par,
  });
  const corrections = EcrituresPaieGenerator.generer([p.bulletinCorrige], [p.salarie], {
    comptes: p.comptes,
    periode_comptable: p.periodeImputation,
    genere_par: p.genere_par,
  }).map<Ecriture>((e) => ({
    ...e,
    source: 'paie_correction',
    periode_origine: p.bulletinCorrige.periode,
    libelle: `Correction — ${e.libelle}`,
  }));
  const total = totauxPiece([...extournes, ...corrections]);
  if (roundFCFA(total.debit) !== roundFCFA(total.credit)) {
    throw new PieceDesequilibreeError(total.debit, total.credit);
  }
  return { extournes, corrections };
}
